import axios from 'axios';

import createUrlWithParams from 'helpers/createUrlWithParams';

import {
  parseAssetProcessingActivitiesForFE,
  parseAssetProcessingActivitiesMetricsForFE,
  parseNeverPublishedProcessingActivityForFE,
  parseNeverPublishedProcessingActivitySaveForBE,
  parseProcessingActivitiesForFE,
  parseProcessingActivitiesMetricsForFE,
  parseProcessingActivityDetailForFE,
  parseProcessingActivityPublishForBE,
  parseRopaCategoriesForFE,
  parseSharedDraftOutputForFE,
  parseSharedDraftWritePayloadForBE,
} from './processingActivities.parsers';
import type {
  AssetProcessingActivitiesMetricsDTO,
  AssetProcessingActivityListDTO,
  NeverPublishedProcessingActivityOutputDTO,
  PaginatedAssetProcessingActivitiesResponse,
  PaginatedProcessingActivitiesResponse,
  PaginatedRopaCategoriesResponse,
  ProcessingActivitiesApi,
  ProcessingActivitiesMetricsDTO,
  ProcessingActivityCreateDTO,
  ProcessingActivityDetail,
  ProcessingActivityDetailDTO,
  ProcessingActivityDetailParams,
  ProcessingActivityListDTO,
  ProcessingActivityPublishInput,
  NeverPublishedProcessingActivitySaveInput,
  RopaCategoryDTO,
  RopaCategoryListParams,
  SharedDraftOutput,
  SharedDraftOutputDTO,
  SharedDraftSaveResponseDTO,
  SharedDraftWritePayload,
} from './processingActivities.types';

export type ProcessingActivityEditorLoadResult =
  | { mode: 'never-published'; data: ProcessingActivityDetail }
  | { mode: 'shared-draft'; data: SharedDraftOutput };

type PaginatedProcessingActivitiesDTOResponse = {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: ProcessingActivityListDTO[];
};

const buildEndpoint = (organizationId: number) => `api/v1/organizations/${organizationId}/processing-activities/`;

const buildDetailEndpoint = (organizationId: number, processingActivityId: number) =>
  `${buildEndpoint(organizationId)}${processingActivityId}/`;

const buildDraftEndpoint = (organizationId: number, processingActivityId: number) =>
  `${buildDetailEndpoint(organizationId, processingActivityId)}draft/`;

const buildDraftOpenEndpoint = (organizationId: number, processingActivityId: number) =>
  `${buildDraftEndpoint(organizationId, processingActivityId)}open/`;

const buildPublishEndpoint = (organizationId: number, processingActivityId: number) =>
  `${buildDetailEndpoint(organizationId, processingActivityId)}publish/`;

const buildArchiveEndpoint = (organizationId: number, processingActivityId: number) =>
  `${buildDetailEndpoint(organizationId, processingActivityId)}archive/`;

const buildMetricsEndpoint = (organizationId: number) => `api/v1/organizations/${organizationId}/processing-activities/metrics/`;

const buildRopaCategoriesEndpoint = (organizationId: number) => `api/v1/organizations/${organizationId}/ropa-categories/`;

const buildAssetProcessingActivitiesEndpoint = (organizationId: number, processingActivityId: number) =>
  `${buildDetailEndpoint(organizationId, processingActivityId)}asset-processing-activities/`;

const buildAssetProcessingActivitiesMetricsEndpoint = (organizationId: number, processingActivityId: number) =>
  `${buildAssetProcessingActivitiesEndpoint(organizationId, processingActivityId)}metrics/`;

const parseListResponse = (data: unknown): PaginatedProcessingActivitiesResponse => {
  if (Array.isArray(data)) {
    return {
      count: data.length,
      next: null,
      previous: null,
      results: parseProcessingActivitiesForFE(data as ProcessingActivityListDTO[]),
    };
  }

  const paginated = data as PaginatedProcessingActivitiesDTOResponse;
  return {
    count: paginated.count ?? 0,
    next: paginated.next ?? null,
    previous: paginated.previous ?? null,
    results: parseProcessingActivitiesForFE(paginated.results ?? []),
  };
};

const parseRopaCategoriesResponse = (data: unknown): PaginatedRopaCategoriesResponse => {
  if (Array.isArray(data)) {
    const results = parseRopaCategoriesForFE(data as RopaCategoryDTO[]);
    return {
      count: results.length,
      next: null,
      previous: null,
      results,
    };
  }

  if (data && typeof data === 'object' && 'results' in data && Array.isArray((data as { results: RopaCategoryDTO[] }).results)) {
    const paginated = data as PaginatedRopaCategoriesResponse;
    return {
      count: paginated.count ?? paginated.results.length,
      next: paginated.next ?? null,
      previous: paginated.previous ?? null,
      results: parseRopaCategoriesForFE(paginated.results),
    };
  }

  if (data && typeof data === 'object' && 'id' in data && 'name' in data) {
    const results = parseRopaCategoriesForFE([data as RopaCategoryDTO]);
    return {
      count: 1,
      next: null,
      previous: null,
      results,
    };
  }

  return {
    count: 0,
    next: null,
    previous: null,
    results: [],
  };
};

const parseNeverPublishedResponse = (data: unknown) => {
  if (data && typeof data === 'object' && 'id' in data) {
    return parseNeverPublishedProcessingActivityForFE(data as NeverPublishedProcessingActivityOutputDTO);
  }

  throw new Error('Processing activity response did not include an id.');
};

type PaginatedAssetProcessingActivitiesDTOResponse = {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: AssetProcessingActivityListDTO[];
};

const parseAssetProcessingActivitiesResponse = (data: unknown): PaginatedAssetProcessingActivitiesResponse => {
  if (Array.isArray(data)) {
    const results = parseAssetProcessingActivitiesForFE(data as AssetProcessingActivityListDTO[]);
    return {
      count: results.length,
      next: null,
      previous: null,
      results,
    };
  }

  if (
    data &&
    typeof data === 'object' &&
    'results' in data &&
    Array.isArray((data as PaginatedAssetProcessingActivitiesDTOResponse).results)
  ) {
    const paginated = data as PaginatedAssetProcessingActivitiesDTOResponse;
    return {
      count: paginated.count ?? paginated.results?.length ?? 0,
      next: paginated.next ?? null,
      previous: paginated.previous ?? null,
      results: parseAssetProcessingActivitiesForFE(paginated.results ?? []),
    };
  }

  if (data && typeof data === 'object' && 'id' in data && 'asset_name' in data) {
    const results = parseAssetProcessingActivitiesForFE([data as AssetProcessingActivityListDTO]);
    return {
      count: 1,
      next: null,
      previous: null,
      results,
    };
  }

  return {
    count: 0,
    next: null,
    previous: null,
    results: [],
  };
};

export const processingActivitiesApi: ProcessingActivitiesApi = {
  getAll: async (organizationId, params) => {
    const { data } = await axios.get(createUrlWithParams(buildEndpoint(organizationId), params));
    return { data: parseListResponse(data) };
  },
  getMetrics: async organizationId => {
    const { data } = await axios.get(buildMetricsEndpoint(organizationId));
    return { data: parseProcessingActivitiesMetricsForFE(data as ProcessingActivitiesMetricsDTO) };
  },
  getDetail: async (organizationId, processingActivityId, params?: ProcessingActivityDetailParams) => {
    const { data } = await axios.get(createUrlWithParams(buildDetailEndpoint(organizationId, processingActivityId), params));
    return { data: parseProcessingActivityDetailForFE(data as ProcessingActivityDetailDTO) };
  },
  create: async (organizationId, payload: ProcessingActivityCreateDTO) => {
    const { data } = await axios.post(buildEndpoint(organizationId), payload);
    return { data: parseNeverPublishedResponse(data) };
  },
  updateNeverPublished: async (organizationId, processingActivityId, payload: NeverPublishedProcessingActivitySaveInput) => {
    const { data } = await axios.patch(
      buildDetailEndpoint(organizationId, processingActivityId),
      parseNeverPublishedProcessingActivitySaveForBE(payload),
    );
    return { data: parseNeverPublishedResponse(data) };
  },
  archive: async (organizationId, processingActivityId) => {
    const { data } = await axios.post(buildArchiveEndpoint(organizationId, processingActivityId));
    return { data: parseNeverPublishedResponse(data) };
  },
  getDraft: async (organizationId, processingActivityId) => {
    const { data } = await axios.get(buildDraftEndpoint(organizationId, processingActivityId));
    return { data: parseSharedDraftOutputForFE(data as SharedDraftOutputDTO) };
  },
  openDraft: async (organizationId, processingActivityId) => {
    const { data } = await axios.post(buildDraftOpenEndpoint(organizationId, processingActivityId), {});
    return { data: parseSharedDraftOutputForFE(data as SharedDraftOutputDTO) };
  },
  updateDraft: async (organizationId, processingActivityId, payload: SharedDraftWritePayload) => {
    const { data } = await axios.patch(
      buildDraftEndpoint(organizationId, processingActivityId),
      parseSharedDraftWritePayloadForBE(payload),
    );
    return { data: parseSharedDraftOutputForFE(data as SharedDraftSaveResponseDTO) };
  },
  deleteDraft: async (organizationId, processingActivityId) => {
    await axios.delete(buildDraftEndpoint(organizationId, processingActivityId));
  },
  publish: async (organizationId, processingActivityId, payload: ProcessingActivityPublishInput) => {
    const { data } = await axios.post(
      buildPublishEndpoint(organizationId, processingActivityId),
      parseProcessingActivityPublishForBE(payload),
    );
    return { data: parseNeverPublishedResponse(data) };
  },
  getRopaCategories: async (organizationId, params: RopaCategoryListParams) => {
    const { data } = await axios.get(createUrlWithParams(buildRopaCategoriesEndpoint(organizationId), params));
    return { data: parseRopaCategoriesResponse(data) };
  },
  getAssetProcessingActivities: async (organizationId, processingActivityId, params) => {
    const { data } = await axios.get(
      createUrlWithParams(buildAssetProcessingActivitiesEndpoint(organizationId, processingActivityId), params),
    );
    return { data: parseAssetProcessingActivitiesResponse(data) };
  },
  getAssetProcessingActivitiesMetrics: async (organizationId, processingActivityId) => {
    const { data } = await axios.get(buildAssetProcessingActivitiesMetricsEndpoint(organizationId, processingActivityId));
    return { data: parseAssetProcessingActivitiesMetricsForFE(data as AssetProcessingActivitiesMetricsDTO) };
  },
};

export const loadProcessingActivityDraft = async (organizationId: number, processingActivityId: number) => {
  try {
    return await processingActivitiesApi.getDraft(organizationId, processingActivityId);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return processingActivitiesApi.openDraft(organizationId, processingActivityId);
    }

    throw error;
  }
};

export const loadProcessingActivityForEditor = async (
  organizationId: number,
  processingActivityId: number,
): Promise<{ data: ProcessingActivityEditorLoadResult }> => {
  const { data: detail } = await processingActivitiesApi.getDetail(organizationId, processingActivityId);

  if (detail.publishedVersionNumber === null) {
    return { data: { mode: 'never-published', data: detail } };
  }

  const { data: draft } = await loadProcessingActivityDraft(organizationId, processingActivityId);
  return { data: { mode: 'shared-draft', data: draft } };
};

export default processingActivitiesApi;
